/**
 * Notification Centre.
 *
 * Every route is scoped server-side to the signed-in caller from their verified
 * token — there is no user id to pass, and deliberately no way to ask for
 * somebody else's.
 */

import { apiGet, apiPost, buildQuery } from '../client.js';

export const fetchNotifications = (limit) =>
  apiGet(`/api/notifications${buildQuery({ limit })}`).then((data) => ({
    notifications: data?.notifications ?? [],
    unread: data?.unread ?? 0
  }));

/**
 * The top-bar icon's poll. Separate from the list on purpose: this is a single
 * indexed COUNT server-side, so it stays cheap enough to run on a timer for
 * every signed-in user.
 */
export const fetchUnreadCount = () =>
  apiGet('/api/notifications/unread-count').then((data) => data?.unread ?? 0);

export const markNotificationRead = (id) =>
  apiPost(`/api/notifications/${encodeURIComponent(id)}/read`).then((data) => ({ unread: data?.unread }));

/** "Ignore": take one off the caller's own list. Rejects with the server's reason when refused. */
export const ignoreNotification = (id) =>
  apiPost(`/api/notifications/${encodeURIComponent(id)}/ignore`).then((data) => ({ unread: data?.unread }));

export const markAllNotificationsRead = () =>
  apiPost('/api/notifications/read-all').then((data) => ({ marked: data?.marked ?? 0 }));

/* ---- Critical alerts: on call first, everyone if unclaimed ---- */

/** Alerts the caller was sent (all for admins), plus whether they are on call. */
export const fetchAlerts = () =>
  apiGet('/api/alerts').then((data) => ({
    alerts: data?.alerts ?? [],
    onCall: data?.onCall ?? null,
    you: data?.you ?? null,
    escalationMinutes: data?.escalationMinutes ?? 30,
    enabled: data?.enabled !== false,
    desktopKlaxon: data?.desktopKlaxon === true,
    // Per-type behaviour from Admin Settings → Alert behaviour (red pop-up, ignorable before claim).
    behaviour: data?.behaviour && typeof data.behaviour === 'object' ? data.behaviour : null
  }));

/** "I've got it". Rejects with the server's reason (e.g. who already has it). */
export const claimAlert = (id) =>
  apiPost(`/api/alerts/${encodeURIComponent(id)}/claim`).then((data) => data?.alert ?? null);

export const escalateAlert = (id) =>
  apiPost(`/api/alerts/${encodeURIComponent(id)}/escalate`).then((data) => data?.alert ?? null);

/* ---- Desktop (Web Push) alerts — the caller's own devices only ---- */

export const fetchPushPublicKey = () =>
  apiGet('/api/push/vapid-public-key').then((data) => data?.publicKey ?? '');

export const fetchPushStatus = () =>
  apiGet('/api/push/status').then((data) => ({ devices: data?.devices ?? [] }));

export const subscribePush = (subscription) =>
  apiPost('/api/push/subscribe', { body: { subscription } });

export const unsubscribePush = (endpoint) =>
  apiPost('/api/push/unsubscribe', { body: { endpoint } });

export const sendTestPush = () => apiPost('/api/push/test');
